import {
  CustomGetJobsForDropdownQuery,
  CustomGetJobsForDropdownQueryVariables,
  useCustomGetJobsForDropdownQuery,
} from "generated/graphql";
import { useCallback, useMemo } from "react";

export const useGetJobsForDropdown = ({
  variables,
  skip = false,
}: {
  variables: CustomGetJobsForDropdownQueryVariables;
  skip?: boolean;
}) => {
  const {
    data: graphqlData,
    error,
    loading,
    fetchMore: more,
  } = useCustomGetJobsForDropdownQuery({
    variables,
    skip,
  });

  const getData = useCallback(({ data }: { data?: CustomGetJobsForDropdownQuery }) => {
    return data?.getJobsSimple?.data || null;
  }, []);

  const getPaginatorInfo = useCallback(({ data }: { data?: CustomGetJobsForDropdownQuery }) => {
    return data?.getJobsSimple?.paginatorInfo || null;
  }, []);

  const data = useMemo(() => {
    return getData({ data: graphqlData });
  }, [getData, graphqlData]);

  const paginatorInfo = useMemo(() => {
    return getPaginatorInfo({ data: graphqlData });
  }, [getPaginatorInfo, graphqlData]);

  const fetchMore = useCallback(
    async ({ page }: { page: number }) => {
      return more({
        variables: {
          ...variables,
          page,
        },
        updateQuery: (previousQueryResult, { fetchMoreResult }) => {
          if (!fetchMoreResult?.getJobsSimple?.data?.length) {
            return previousQueryResult;
          }

          return {
            ...fetchMoreResult,
            getJobsSimple: {
              ...fetchMoreResult.getJobsSimple,
              data: [
                ...(previousQueryResult.getJobsSimple?.data ?? []),
                ...fetchMoreResult.getJobsSimple.data,
              ],
            },
          };
        },
      });
    },
    [more, variables]
  );

  return { loading, data, error, getData, getPaginatorInfo, paginatorInfo, fetchMore } as const;
};
